import React, { useState, useEffect } from 'react'
import Turnstile from 'react-turnstile'
import { Eye, EyeOff, X, Check, RefreshCw, Gift } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { apiGetJson } from '../../../lib/apiClient'
import { AnimatePresence, motion } from 'framer-motion'
import { backdropAnimation, hoverScale, modalAnimation } from '../../../lib/animations'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  initialMode?: 'login' | 'register' | 'forgot'
}

export default function AuthModal({ isOpen, onClose, initialMode }: AuthModalProps) {
  const [mode, setMode] = useState<'login' | 'register' | 'forgot'>(initialMode || 'login')
  const [username, setUsername] = useState('')
  const [fullName, setFullName] = useState('')
  const [zaloPhone, setZaloPhone] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [usernameValid, setUsernameValid] = useState<boolean | null>(null)
  const [checkingUsername, setCheckingUsername] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [captchaKey, setCaptchaKey] = useState(0)
  const [referrerName, setReferrerName] = useState<string | null>(null)

  const { signIn, signUp, resetPassword } = useAuth()

  useEffect(() => {
    setCaptchaToken(null)
  }, [mode, isOpen])

  // Sync mode when initialMode changes (e.g. opened from ReferralBanner)
  useEffect(() => {
    if (isOpen && initialMode) {
      setMode(initialMode)
    }
  }, [isOpen, initialMode])

  // Fetch referrer info when in register mode
  useEffect(() => {
    if (mode !== 'register') return
    try {
      const params = new URLSearchParams(window.location.search)
      const refId = (params.get('referrer_id') || params.get('ref') || '').trim()
      if (!refId) { setReferrerName(null); return }
      apiGetJson<any>(`/api/auth/referrer-info?id=${refId}`).then(resp => {
        if (resp?.data?.full_name || resp?.data?.username) {
          setReferrerName(resp.data.full_name || resp.data.username)
        }
      }).catch(() => { })
    } catch { setReferrerName(null) }
  }, [mode])

  const checkUsername = async (u: string) => {
    if (!u || u.length < 3) {
      setUsernameValid(null)
      return
    }
    // Regex check first
    if (!/^[a-zA-Z0-9_]{3,50}$/.test(u)) {
      setUsernameValid(false)
      return
    }

    setCheckingUsername(true)
    try {
      const resp = await apiGetJson<{ exists: boolean }>(`/api/auth/check-username?username=${encodeURIComponent(u)}`)
      setUsernameValid(!resp?.exists)
    } catch (err) {
      console.error(err)
      setUsernameValid(null)
    } finally {
      setCheckingUsername(false)
    }
  }



  // Inside component
  const checkUsernameTimeoutRef = React.useRef<any>(null) // Use any to avoid NodeJS types issue if not installed

  const onUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value
    // Strip '@' if present
    val = val.replace(/@/g, '')

    setUsername(val)

    // Reset state
    setUsernameValid(null)

    if (checkUsernameTimeoutRef.current) clearTimeout(checkUsernameTimeoutRef.current)
    checkUsernameTimeoutRef.current = setTimeout(() => {
      checkUsername(val)
    }, 500)
  }

  const reloadCaptcha = () => {
    setCaptchaKey(prev => prev + 1)
    setCaptchaToken(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setMessage('')

    // Check Captcha for login and register
    if (mode !== 'forgot' && !captchaToken && import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY) {
      setMessage('Vui lòng hoàn thành xác thực bảo mật (Captcha)')
      setLoading(false)
      return
    }

    try {
      if (mode === 'login') {
        const { error } = await signIn(email, password, captchaToken || undefined, rememberMe)
        if (error) throw error
        onClose()
      } else if (mode === 'register') {
        if (password !== confirmPassword) {
          throw new Error('Mật khẩu xác nhận không khớp')
        }
        if (!/^[a-zA-Z0-9_]{3,50}$/.test(username)) {
          throw new Error('Tên đăng nhập không hợp lệ (3-50 ký tự, chỉ chữ, số và gạch dưới)')
        }

        // Prevent submit if username invalid
        if (usernameValid === false) {
          throw new Error('Tên đăng nhập đã tồn tại')
        }

        const { data, error } = await signUp(email, password, username, fullName, zaloPhone || undefined, captchaToken || undefined)
        if (error) throw error

        if (data?.session) {
          setMessage('Đăng ký thành công! Đang đăng nhập...')
          setTimeout(() => onClose(), 1500)
        } else {
          setMessage('Đăng ký thành công! Bây giờ bạn có thể đăng nhập.')
          setMode('login')
        }
      } else if (mode === 'forgot') {
        const { error, data } = await resetPassword(email)
        if (error) throw error

        // Display message based on method
        if (data?.method === 'admin_required') {
          setMessage('⚠️ IP/Thiết bị chưa được ghi nhận. Yêu cầu đã được gửi đến Admin để xác minh. Vui lòng chờ phản hồi.')
        } else if (data?.method === 'admin_pending') {
          setMessage('⏳ Bạn đã có yêu cầu khôi phục đang chờ Admin xử lý.')
        } else {
          setMessage(data?.message || 'Email khôi phục mật khẩu đã được gửi!')
        }
      }
    } catch (error: any) {
      if (error.message === 'Invalid login credentials') {
        setMessage('Email hoặc mật khẩu không đúng. Vui lòng kiểm tra lại hoặc đăng ký tài khoản mới.')
      } else if (error.message === 'User already registered') {
        setMessage('Email này đã được đăng ký. Vui lòng đăng nhập hoặc sử dụng email khác.')
      } else if (error.message?.includes('captcha')) {
        setMessage('Lỗi xác thực Captcha. Vui lòng nhấn "Tải lại Captcha" và thử lại.')
        setCaptchaToken(null)
      } else {
        setMessage(error.message || 'Có lỗi xảy ra')
      }
    } finally {
      setLoading(false)
    }
  }

  const isPositiveMessage = message.includes('thành công') || message.includes('gửi') || message.includes('đã được')
  const isCompactMode = mode !== 'register'
  const compactHeightClass = isCompactMode
    ? 'h-[min(32rem,calc(100vh-2rem))]'
    : 'max-h-[85vh]'

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          {...backdropAnimation}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center p-4 pb-[env(safe-area-inset-bottom,0.5rem)] overflow-y-auto z-50"
        >
          <motion.div
            {...modalAnimation}
            className={`bg-white dark:bg-slate-900 rounded-lg w-full sm:w-[600px] sm:max-w-[90vw] my-auto ${compactHeightClass} overflow-y-auto`}
          >
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold">
                  {mode === 'login' && 'Đăng nhập'}
                  {mode === 'register' && 'Đăng ký tài khoản'}
                  {mode === 'forgot' && 'Quên mật khẩu'}
                </h2>
                {mode === 'register' && (
                  <p className="text-sm text-gray-500 mt-1">
                    Hoặc <motion.button type="button" {...hoverScale} onClick={() => setMode('login')} className="text-amber-600 hover:underline">đăng nhập</motion.button> nếu đã có tài khoản
                  </p>
                )}
              </div>
              <motion.button
                type="button"
                {...hoverScale}
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </motion.button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {mode === 'register' && (
                <>
                  {/* Referrer info box */}
                  {(() => {
                    const params = new URLSearchParams(window.location.search)
                    const refId = (params.get('referrer_id') || params.get('ref') || '').trim()
                    if (!refId) return null
                    return (
                      <div className="flex items-center gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-700/30 rounded-lg px-3 py-2.5">
                        <Gift className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                        <div className="text-sm">
                          <span className="text-amber-700 dark:text-amber-300">Mã giới thiệu: </span>
                          <code className="font-mono text-amber-800 dark:text-amber-200 bg-white/60 dark:bg-slate-800/40 px-1.5 py-0.5 rounded text-xs">{refId.substring(0, 8).toUpperCase()}</code>
                          {referrerName && (
                            <span className="text-amber-600 dark:text-amber-400 ml-1">— từ <strong>{referrerName}</strong></span>
                          )}
                        </div>
                      </div>
                    )
                  })()}
                  <div>
                    <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                      Tên đăng nhập
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="username"
                        value={username}
                        onChange={onUsernameChange}
                        className={`w-full px-3 py-2 border rounded-md focus:ring-amber-500 focus:border-amber-500 pr-10 ${usernameValid === true ? 'border-green-500 bg-green-50' :
                          usernameValid === false ? 'border-red-500 bg-red-50' :
                            'border-gray-300'
                          }`}
                        placeholder="Username123"
                        required
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        {checkingUsername ? (
                          <RefreshCw className="h-4 w-4 text-amber-500 animate-spin" />
                        ) : usernameValid === true ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : usernameValid === false ? (
                          <X className="h-4 w-4 text-red-500" />
                        ) : null}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">3-50 ký tự, chỉ chữ, số và gạch dưới</p>
                    {usernameValid === false && <p className="text-xs text-red-600 mt-1">Tên đăng nhập đã tồn tại hoặc không hợp lệ</p>}
                  </div>

                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                      Họ tên
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                      placeholder="Nguyễn Văn A"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="zaloPhone" className="block text-sm font-medium text-gray-700 mb-2">
                      Số điện thoại Zalo (tùy chọn)
                    </label>
                    <input
                      type="tel"
                      id="zaloPhone"
                      value={zaloPhone}
                      onChange={(e) => setZaloPhone(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                      placeholder="VD: 0987654321"
                    />
                  </div>
                </>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {mode === 'login' ? 'Email / Tên đăng nhập / SĐT Zalo' : 'Email (để khôi phục tài khoản)'}
                </label>
                <input
                  type={mode === 'login' ? 'text' : 'email'}
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                  placeholder={mode === 'register' ? "Your@email.com" : "Email / Tên đăng nhập / SĐT Zalo"}
                  required
                />
              </div>

              {mode !== 'forgot' && (
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Mật khẩu
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500 pr-10"
                      placeholder={mode === 'register' ? "Tối thiểu 6 ký tự" : "Nhập Mật khẩu"}
                      required
                      minLength={6}
                    />
                    <motion.button
                      {...hoverScale}
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </motion.button>
                  </div>
                </div>
              )}

              {mode === 'login' && (
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                    Ghi nhớ đăng nhập
                  </label>
                </div>
              )}

              {mode === 'register' && (
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Xác nhận mật khẩu
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500 pr-10"
                      placeholder="Nhập lại Mật khẩu"
                      required
                      minLength={6}
                    />
                  </div>
                </div>
              )}

              {/* Cloudflare Turnstile */}
              {mode !== 'forgot' && import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY && (
                <div className="flex flex-col items-center justify-center my-4">
                  <div className="relative">
                    <Turnstile
                      key={`turnstile-${mode}-${captchaKey}`}
                      sitekey={import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY}
                      onVerify={(token) => setCaptchaToken(token)}
                      onExpire={() => setCaptchaToken(null)}
                      onError={() => setCaptchaToken(null)}
                    />
                  </div>
                  <motion.button
                    {...hoverScale}
                    type="button"
                    onClick={reloadCaptcha}
                    className="text-xs text-amber-600 hover:text-blue-800 mt-2 flex items-center gap-1"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Tải lại Captcha
                  </motion.button>
                </div>
              )}

              {message && (
                <motion.div
                  key={message}
                  initial={{ opacity: 0, y: -6 }}
                  animate={isPositiveMessage
                    ? { opacity: 1, y: 0 }
                    : { opacity: 1, y: 0, x: [0, -8, 8, -6, 6, -3, 3, 0] }}
                  transition={{ duration: 0.45 }}
                  className={`p-3 rounded-md text-sm ${isPositiveMessage
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700'
                    }`}
                >
                  {message}
                </motion.div>
              )}

              <motion.button
                {...hoverScale}
                type="submit"
                disabled={loading}
                className="w-full bg-amber-600 text-white py-2 px-4 rounded-md hover:bg-amber-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Đang xử lý...' :
                  mode === 'login' ? 'Đăng nhập' :
                    mode === 'register' ? 'Đăng ký' : 'Gửi email khôi phục'}
              </motion.button>

              <div className="text-center space-y-2 text-sm">
                {mode === 'login' && (
                  <>
                    <div>
                      <span className="text-gray-600">Chưa có tài khoản? </span>
                      <motion.button
                        {...hoverScale}
                        type="button"
                        onClick={() => setMode('register')}
                        className="text-amber-600 hover:text-blue-800"
                      >
                        Đăng ký
                      </motion.button>
                    </div>
                    <div>
                      <motion.button
                        {...hoverScale}
                        type="button"
                        onClick={() => setMode('forgot')}
                        className="text-gray-500 hover:text-amber-600"
                      >
                        Quên mật khẩu?
                      </motion.button>
                    </div>
                  </>
                )}

                {mode === 'register' && (
                  <div>
                    <span className="text-gray-600">Đã có tài khoản? </span>
                    <motion.button
                      {...hoverScale}
                      type="button"
                      onClick={() => setMode('login')}
                      className="text-amber-600 hover:text-blue-800"
                    >
                      Đăng nhập
                    </motion.button>
                  </div>
                )}

                {mode === 'forgot' && (
                  <motion.button
                    {...hoverScale}
                    type="button"
                    onClick={() => setMode('login')}
                    className="text-amber-600 hover:text-blue-800"
                  >
                    Quay lại đăng nhập
                  </motion.button>
                )}
              </div>
            </form>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}
